import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Card,
    CardContent,
    TextField,
    Button,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Chip,
    IconButton,
    Divider
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Save as SaveIcon,
    Category as CategoryIcon,
    Palette as PaletteIcon
} from '@mui/icons-material';

const mockCategories = [
    { id: 1, name: 'Outils Marketing', description: 'Applications pour le marketing digital', applicationsCount: 12, color: '#10b981' },
    { id: 2, name: 'Productivité', description: 'Outils pour améliorer la productivité', applicationsCount: 8, color: '#3b82f6' },
    { id: 3, name: 'Communication', description: 'Applications de communication', applicationsCount: 5, color: '#f59e0b' },
    { id: 4, name: 'E-commerce', description: 'Solutions de commerce électronique', applicationsCount: 15, color: '#8b5cf6' },
    { id: 5, name: 'Finances', description: 'Applications financières', applicationsCount: 3, color: '#ef4444' },
    { id: 6, name: 'Développement', description: 'Outils de développement', applicationsCount: 7, color: '#06b6d4' }
];

const availableColors = [
    { name: 'Bleu', value: '#3b82f6' },
    { name: 'Vert', value: '#10b981' },
    { name: 'Orange', value: '#f59e0b' },
    { name: 'Violet', value: '#8b5cf6' },
    { name: 'Rouge', value: '#ef4444' },
    { name: 'Cyan', value: '#06b6d4' },
    { name: 'Rose', value: '#ec4899' },
    { name: 'Indigo', value: '#6366f1' },
    { name: 'Emeraude', value: '#059669' },
    { name: 'Amber', value: '#d97706' }
];

const CategoryFormPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditing = id && id !== 'new';

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        color: '#3b82f6'
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isEditing) {
            const category = mockCategories.find(cat => cat.id === parseInt(id));
            if (category) {
                setFormData({
                    name: category.name,
                    description: category.description,
                    color: category.color
                });
            }
        }
    }, [id, isEditing]);

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Le nom de la catégorie est requis';
        }

        if (!formData.description.trim()) {
            newErrors.description = 'La description est requise';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        setLoading(true);

        try {
            await new Promise(resolve => setTimeout(resolve, 1000));
            console.log('Sauvegarde de la catégorie:', formData);
            navigate('/categories');
        } catch (error) {
            console.error('Erreur lors de la sauvegarde:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate('/categories');
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <IconButton onClick={handleBack} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <CategoryIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    {isEditing ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
                </Typography>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ mb: 3, color: 'text.primary' }}>
                                Informations générales
                            </Typography>

                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Nom de la catégorie"
                                        value={formData.name}
                                        onChange={(e) => handleChange('name', e.target.value)}
                                        error={!!errors.name}
                                        helperText={errors.name}
                                        placeholder="Ex: Outils Marketing"
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Description"
                                        value={formData.description}
                                        onChange={(e) => handleChange('description', e.target.value)}
                                        error={!!errors.description}
                                        helperText={errors.description}
                                        placeholder="Description de la catégorie"
                                        multiline
                                        rows={3}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Couleur</InputLabel>
                                        <Select
                                            value={formData.color}
                                            onChange={(e) => handleChange('color', e.target.value)}
                                            label="Couleur"
                                        >
                                            {availableColors.map((color) => (
                                                <MenuItem key={color.value} value={color.value}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Box
                                                            sx={{
                                                                width: 20,
                                                                height: 20,
                                                                bgcolor: color.value,
                                                                borderRadius: '50%',
                                                                mr: 2
                                                            }}
                                                        />
                                                        {color.name}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none', mb: 3 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                                Aperçu
                            </Typography>

                            <Box sx={{ mb: 3 }}>
                                <Chip
                                    label={formData.name || 'Nom de la catégorie'}
                                    sx={{
                                        bgcolor: formData.color,
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: '0.875rem'
                                    }}
                                />
                            </Box>

                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                                {formData.description || 'Description de la catégorie'}
                            </Typography>

                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <PaletteIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Couleur sélectionnée
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    bgcolor: formData.color,
                                    borderRadius: 1,
                                    mt: 1,
                                    border: '2px solid',
                                    borderColor: 'divider'
                                }}
                            />
                        </CardContent>
                    </Card>

                    <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                                Actions
                            </Typography>

                            <Button
                                fullWidth
                                variant="contained"
                                color="success"
                                startIcon={<SaveIcon />}
                                onClick={handleSave}
                                disabled={loading}
                                sx={{ mb: 2 }}
                            >
                                {loading ? 'Sauvegarde...' : (isEditing ? 'Mettre à jour' : 'Créer la catégorie')}
                            </Button>

                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={handleBack}
                                sx={{
                                    borderColor: 'divider',
                                    color: 'text.secondary',
                                    '&:hover': {
                                        borderColor: 'text.secondary',
                                        bgcolor: 'action.hover'
                                    }
                                }}
                            >
                                Annuler
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {Object.keys(errors).length > 0 && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    Veuillez corriger les erreurs dans le formulaire.
                </Alert>
            )}
        </Box>
    );
};

export default CategoryFormPage;